import React, { useState, useEffect } from 'react';

const Connect2Showcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const features = [
    {
      title: 'Virtual Spaces',
      description: 'Navigate through virtual rooms where users can freely explore and choose their interactions',
      icon: '🏠'
    },
    {
      title: 'Real-time Communication',
      description: 'Engage in conversations that mimic the fluidity of in-person interactions',
      icon: '💬'
    },
    {
      title: 'Personalized Experience',
      description: 'Create your own space and customize how you connect with others',
      icon: '✨'
    },
    {
      title: 'Multiformat Content',
      description: 'Share and discover content in various formats within virtual environments',
      icon: '📸'
    }
  ];

  const teamMembers = [
    { name: 'Hailey', role: 'Design', color: 'from-pink-400 to-rose-500' },
    { name: 'Ethan', role: 'Frontend/Full Stack', color: 'from-blue-400 to-indigo-500' },
    { name: 'Arunav', role: 'Backend/API', color: 'from-cyan-400 to-blue-500' },
    { name: 'Zuhair', role: 'Backend/API', color: 'from-green-400 to-emerald-500' },
    { name: 'Fume', role: 'Frontend', color: 'from-purple-400 to-violet-500' },
    { name: 'Gavin', role: 'Deployment', color: 'from-orange-400 to-amber-500' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/20 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Hero Section */}
      <header className="relative py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
            <span className="text-sm font-medium">🏆 D.O. IT Hackathon 2024</span>
          </div>

          <h1 className="text-6xl md:text-8xl font-bold mb-6">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Connect
            </span>
            <span className="text-white"> 2</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            Reimagining social media through 2D virtual spaces that transform 
            online interactions beyond traditional scrolling
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <a
              href="https://github.com/ARNAB814/connect2"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-lg transition-all"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
              </svg>
              <span>View on GitHub</span>
            </a>
            <a
              href="https://haileykinsella.wordpress.com/connect2"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 px-6 py-3 rounded-lg transition-all shadow-lg shadow-purple-500/30"
            >
              <span>Project Showcase</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-cyan-400">48h</div>
              <div className="text-gray-400 text-sm">Hackathon</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-purple-400">6</div>
              <div className="text-gray-400 text-sm">Team Members</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-pink-400">∞</div>
              <div className="text-gray-400 text-sm">Possibilities</div>
            </div>
          </div>
        </div>
      </header>

      {/* The Problem */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6 text-center">The Problem with Social Media</h2>
            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              <p>
                Traditional social media forces us into <span className="text-white font-semibold">one-directional scrolling</span>—an 
                endless feed where algorithms decide what we see and who we interact with.
              </p>
              <p>
                Real social interaction doesn't work this way. In real life, we <span className="text-cyan-400">walk into rooms</span>, 
                <span className="text-purple-400"> choose conversations</span>, and <span className="text-pink-400">move between groups</span> freely.
              </p>
              <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-l-4 border-cyan-400 pl-6 py-4 rounded-r-lg">
                <p className="text-white italic">
                  "What if social media felt more like walking into a party than scrolling through a list?"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">How Connect 2 Works</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-white/5 backdrop-blur-sm border rounded-xl p-8 transition-all duration-500 cursor-pointer ${
                  activeFeature === index 
                    ? 'border-purple-400 scale-105 shadow-xl shadow-purple-500/20' 
                    : 'border-white/10 hover:border-white/30'
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Built With</h2>
          
          <div className="flex flex-wrap justify-center gap-4">
            {['React', 'TypeScript', 'Vite', 'PocketBase', 'Figma', 'Real-time WebSockets'].map((tech, index) => (
              <div
                key={tech}
                className="bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full text-white font-medium hover:bg-white/20 transition-all"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="relative py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">The Team</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {teamMembers.map((member, index) => (
              <div
                key={member.name}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:border-white/30 transition-all group"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${member.color} rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white group-hover:scale-110 transition-transform`}>
                  {member.name.charAt(0)}
                </div>
                <h4 className="font-semibold text-white">{member.name}</h4>
                <p className="text-gray-400 text-sm">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 border border-white/10 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-6">The Vision</h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Connect 2 isn't just another social platform—it's a fundamental reimagining of 
              how we interact online. By bringing spatial awareness to digital socializing, 
              we create opportunities for the serendipitous encounters and natural conversations 
              that make real-world interactions so valuable.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">No Algorithm</span>
              <span className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm">User Choice</span>
              <span className="px-4 py-2 bg-pink-500/20 text-pink-300 rounded-full text-sm">Real Connection</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 mb-4">
            Connect 2 — D.O. IT (Don't Overthink It) Hackathon 2024
          </p>
          <div className="flex justify-center gap-6">
            <a
              href="https://github.com/ARNAB814/connect2"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              GitHub
            </a>
            <a
              href="https://haileykinsella.wordpress.com/connect2"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Project Page
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Connect2Showcase;
